/**
 * ContractorScheduleReplyCapture · iter303ak-fix28 — Work Booked · T30 · Action 20
 * ───────────────────────────────────────────────────────────────────────────────
 * Log each contractor's schedule reply: Confirmed / Rescheduled / Pending
 * + optional note + timestamp. Rows are hydrated from the previous action's
 * send_state (only contractors who received the request are listed).
 *
 * Persists to parent appraisal as:
 *   contractor_schedule_replies: {
 *     [child_id]: { [contractor_id]: { status, note, at } }
 *   }
 * Also writes the mirror to captured_data under `write_to` / persist_field.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, MessageCircle, Clock, AlertTriangle, User } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const STATUSES = [
  { key: 'confirmed',   label: 'Confirmed',
    active: 'bg-emerald-600 text-white border-emerald-700',
    inactive: 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50' },
  { key: 'rescheduled', label: 'Rescheduled',
    active: 'bg-amber-600 text-white border-amber-700',
    inactive: 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50' },
  { key: 'pending',     label: 'Pending',
    active: 'bg-slate-600 text-white border-slate-700',
    inactive: 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50' },
];

export default function ContractorScheduleReplyCapture({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;
  const persistField = params.persist_field || 'contractor_schedule_replies';

  // Rows come from the previous action's send_state.
  const sentRows = useMemo(() => {
    const sendState = capturedData?.contractor_schedule_send_state?.send_state
      || capturedData?.contractor_schedule_send_state
      || {};
    const rowState = capturedData?.contractor_schedule_send_state?.row_state || {};
    const items = [];
    for (const [key, st] of Object.entries(sendState)) {
      if (!st?.sent_at) continue;
      const [child_id, contractor_id] = key.split('|');
      if (!child_id || !contractor_id) continue;
      items.push({ child_id, contractor_id, sent_at: st.sent_at, to_email: st.to_email, key, contractor_name: rowState[key]?.contractor_name });
    }
    return items;
  }, [capturedData]);

  // Enrich rows with contractor + child metadata for display.
  const [items, setItems] = useState([]);
  const [replies, setReplies] = useState(() => value?.replies || {});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const enrich = useCallback(async () => {
    if (!sentRows.length) return;
    const decisions = capturedData?.appraisal_record?.child_decisions
      || capturedData?.appraisal?.child_decisions
      || {};
    const enrichedItems = [];
    for (const it of sentRows) {
      const child = decisions[it.child_id] || {};
      let contractor_name = it.contractor_name || '';
      if (!contractor_name) {
        try {
          const { data: cdoc } = await api.get(`/contractors/${it.contractor_id}`);
          contractor_name = cdoc?.contractor_name || cdoc?.company_name || '';
        } catch { /* silent */ }
      }
      enrichedItems.push({
        ...it,
        contractor_name,
        appraisal_no: child.appraisal_no,
        quote_type_ref: child.quote_type_ref,
        variant_label: child.variant_label,
      });
    }
    setItems(enrichedItems);
  }, [sentRows, capturedData]);

  useEffect(() => { enrich(); }, [enrich]);

  const setReply = (child_id, contractor_id, patch) => {
    setReplies(prev => ({
      ...prev,
      [child_id]: {
        ...(prev[child_id] || {}),
        [contractor_id]: {
          ...((prev[child_id] || {})[contractor_id] || {}),
          ...patch,
          at: new Date().toISOString(),
        },
      },
    }));
  };

  useEffect(() => {
    if (onChange) onChange({ replies });
  }, [replies]);   

  const persistAndContinue = async () => {
    setSaving(true);
    setSaveErr('');
    try {
      if (parentAppraisalId) {
        await api.patch(`/appraisals/${parentAppraisalId}`, {
          [persistField]: replies,
        });
      }
      onNext();
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const totalRows = items.length;
  const answeredRows = items.filter(it => {
    const r = (replies[it.child_id] || {})[it.contractor_id];
    return r?.status && r.status !== 'pending';
  }).length;
  const allAnswered = totalRows > 0 && answeredRows === totalRows;

  return (
    <div className="space-y-4" data-testid="wf-contractor-schedule-reply">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <MessageCircle className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Log Contractor Replies</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Log each contractor\'s reply. Continue is enabled once every contractor is marked Confirmed or Rescheduled.'}
            </div>
          </div>
        </div>
      </div>

      {items.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No contractor sends detected in the previous action. Go back and send at least one confirmation.
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map(it => {
            const reply = (replies[it.child_id] || {})[it.contractor_id] || {};
            const label = `${it.quote_type_ref || ''} — ${it.contractor_name || it.contractor_id}`;
            return (
              <div key={`${it.child_id}|${it.contractor_id}`}
                className={`p-3 border rounded-lg ${reply.status && reply.status !== 'pending' ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-slate-200'}`}
                data-testid={`wf-cr-row-${it.child_id}-${it.contractor_id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-sm font-medium text-slate-800">{label}</span>
                  {it.appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{it.appraisal_no}</span>}
                  {it.sent_at && (
                    <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                      <Clock size={10} /> Sent {new Date(it.sent_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {STATUSES.map(s => {
                    const active = reply.status === s.key;
                    return (
                      <button key={s.key}
                        type="button"
                        onClick={() => setReply(it.child_id, it.contractor_id, { status: s.key })}
                        data-testid={`wf-cr-status-${it.child_id}-${it.contractor_id}-${s.key}`}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${active ? s.active : s.inactive}`}>
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                <input type="text" value={reply.note || ''}
                  onChange={(e) => setReply(it.child_id, it.contractor_id, { status: reply.status || 'pending', note: e.target.value })}
                  placeholder="Optional note (e.g. requested delay, alt date …)"
                  data-testid={`wf-cr-note-${it.child_id}-${it.contractor_id}`}
                  className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
              </div>
            );
          })}
        </div>
      )}

      {saveErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{saveErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-cr-back">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={persistAndContinue} disabled={!allAnswered || saving}
            data-testid="wf-cr-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
